/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */


import { FeatureTag } from '@g17eco/core';
import { AppCode, AppConfig } from '@g17eco/types/app';
import { RootInitiativeData } from '@g17eco/types/initiative';
import { Subscription } from '../types/subscriptions';
import { ProductCodes } from '../types/products';
import { PERMISSION_GROUPS } from '@utils/permission-groups';


// ////////////////////////////////////////////////////
//
// File for resolving AppCode related things in a single place
// Should be easy to introduce new AppCode and do the changes in maximum 2 files
//
// ////////////////////////////////////////////////////


/**
 * @TODO: Can we drop and rely on productCode or validProductCodes?
 * These are used to determine if we can redirect to CT with appConfig
 * should probably be called tiers or similar.
 */
export const companyTrackerAppCodes = [
  AppCode.CompanyTracker,
  AppCode.CompanyTrackerPro,
  AppCode.CompanyTrackerEnterprise,
  AppCode.CompanyTrackerStarter,
  AppCode.TOLI,
  AppCode.WWG,
  AppCode.AseanBundleGPT,
  AppCode.AseanBundleUnravel,
];

export const getAppTypeLabel = (appCode?: AppCode | ''): string | undefined=> {
  switch (appCode) {
    case AppCode.CompanyTracker:
      return 'COMPANY TRACKER';
    case AppCode.CompanyTrackerStarter:
      return 'COMPANY TRACKER STARTER'
    case AppCode.CompanyTrackerPro:
      return 'COMPANY TRACKER PRO';
    case AppCode.CompanyTrackerEnterprise:
      return 'COMPANY TRACKER ENTERPRISE';
    case AppCode.AseanBundleGPT:
      return 'COMPANY TRACKER BUNDLE';
    case AppCode.AseanBundleUnravel:
      return 'COMPANY TRACKER BUNDLE';
    // Deprecated: SGX retired; remove once no orgs have this code (GU-7237)
    case AppCode.SGXESGenome:
      return 'SGX ESGENOME';
    case AppCode.TOLI:
      return 'TOLI';
    case AppCode.WWG:
      return 'WWG';
    case AppCode.EmissionTracker:
      return 'EMISSIONS TRACKER';
    case AppCode.MaterialityTracker:
      return 'MATERIALITY TRACKER';
    case AppCode.PortfolioTrackerExchange:
      return 'PORTFOLIO TRACKER EXCHANGE';
    case AppCode.PortfolioTrackerExchangeSGX:
      return 'PORTFOLIO TRACKER EXCHANGE SGX';
    default:
      return;
  }
}

const getBadgeText = (code?: AppCode) => {
  switch (code) {
    case AppCode.CompanyTrackerPro:
      return 'Pro';
    case AppCode.CompanyTrackerEnterprise:
      return 'Enterprise';
    case AppCode.CompanyTrackerStarter:
      return 'Starter';
    case AppCode.TOLI:
      return 'TOLI';
    case AppCode.WWG:
      return 'WWG';
    default:
      return undefined;
  }
}

interface AppBadgeProps {
  appConfig?: AppConfig;
  isStaffOrganization?: boolean;
  isDemo?: boolean;
}

export const getAppBadgeCodes = ({ appConfig, isStaffOrganization, isDemo }: AppBadgeProps): string[] => {
  const codes: string[] = [];
  const tierBadgeLabel = getBadgeText(appConfig?.code);
  if (tierBadgeLabel) {
    codes.push(tierBadgeLabel);
  }

  if (isStaffOrganization || isDemo) {
    codes.push('Demo');
  }
  return codes;
}


// Determine pure MT by checking appConfigCode and permissionGroup, or fall back to subscriptions if integrated via CT
export const isMaterialityTracker = (company: Pick<RootInitiativeData, 'appConfigCode' | 'appConfig' | 'permissionGroup'>, subscriptions?: Subscription[]) => {
  const appConfigCode = company.appConfigCode || company.appConfig?.code;
  return (
    (appConfigCode === AppCode.MaterialityTracker &&
      company.permissionGroup === PERMISSION_GROUPS.COMPANY_TRACKER_STARTER) ||
    !!subscriptions?.some((s) => s.items.some((i) => i.productCode === ProductCodes.MaterialityTracker))
  );
};

export const canAccessMaterialityTracker = ({
  initiative,
  isUserStaff,
}: {
  initiative: Pick<RootInitiativeData, 'appConfigCode' | 'appConfig' | 'tags'>;
  isUserStaff: boolean;
}) => {
  return isUserStaff || !initiative.tags?.includes(FeatureTag.LimitMaterialityTrackerToStaffOnly);
};
